import type { Permission } from '@elsa/contracts';
import { Loading, navigate, Page } from '@elsa/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { OrgProvider, useAuthSession, useOrg, useOrgs } from '../lib/session.tsx';
import { supabase } from '../lib/supabase.ts';
import { AccountPage } from './Account.tsx';
import { AcquisitionPage } from './Acquisition.tsx';
import { AlertsPage } from './Alerts.tsx';
import { AppLayout, allowed, NAV } from './AppLayout.tsx';
import { BillingPage } from './Billing.tsx';
import { EntityPage } from './Entity.tsx';
import { HomePage } from './Home.tsx';
import { NotificationsPage } from './Notifications.tsx';
import { OnboardingPage } from './Onboarding.tsx';
import { CpvPage, PricesPage } from './Prices.tsx';
import { RolesPage } from './Roles.tsx';
import { SearchPage } from './Search.tsx';
import { SettingsPage } from './Settings.tsx';
import { TeamPage } from './Team.tsx';

// Ce permisiune cere o ruta: intrarea din meniu cu cel mai lung prefix, plus fisele.
function needFor(path: string): Permission[] | undefined {
  if (/^\/app\/(autoritati|furnizori)\//.test(path)) return ['data.read'];
  return NAV.filter((n) => n.to !== '/app' && (path === n.to || path.startsWith(`${n.to}/`))).sort(
    (a, b) => b.to.length - a.to.length,
  )[0]?.need;
}

// Poarta aplicatiei: sesiune -> firma (onboarding daca nu are) -> paginile.
export function AppShell({ path }: { path: string }) {
  const session = useAuthSession();
  const orgs = useOrgs(session?.user.id);
  useEffect(() => {
    if (session === null) {
      navigate(`/autentificare?next=${encodeURIComponent(location.pathname + location.search)}`);
    }
  }, [session]);
  if (!session || orgs.isLoading) {
    return (
      <div className="p-6">
        <Loading />
      </div>
    );
  }
  if (orgs.error) {
    return (
      <div className="p-6 text-sm text-semnal-700">
        Nu pot citi firmele contului: {(orgs.error as Error).message}
      </div>
    );
  }
  const list = orgs.data ?? [];
  if (!list.length || path === '/app/firma-noua') {
    return <OnboardingPage first={!list.length} email={session.user.email ?? ''} />;
  }
  return (
    <OrgProvider session={session} orgs={list}>
      <AppLayout path={path}>
        <Routes path={path} />
      </AppLayout>
    </OrgProvider>
  );
}

function useNotificationsRealtime() {
  const { session } = useOrg();
  const qc = useQueryClient();
  useEffect(() => {
    const ch = supabase
      .channel('elsa-notifications')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'app',
          table: 'notifications',
          filter: `user_id=eq.${session.user.id}`,
        },
        () => void qc.invalidateQueries({ queryKey: ['notifications'] }),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [session.user.id, qc]);
}

function Routes({ path }: { path: string }) {
  useNotificationsRealtime();
  const { can } = useOrg();
  if (!allowed(can, needFor(path))) {
    return (
      <Page title="Nu ai acces">
        <p className="text-sm text-gri-700">
          Rolul tău din firmă nu include această pagină. Cere administratorului firmei, dacă ai
          nevoie de ea.
        </p>
      </Page>
    );
  }
  const m = (re: RegExp) => path.match(re)?.[1];
  const acq = m(/^\/app\/achizitii\/(\d+)$/);
  if (acq) return <AcquisitionPage id={Number(acq)} />;
  const ca = m(/^\/app\/autoritati\/(\d+)$/);
  if (ca) return <EntityPage kind="authority" id={Number(ca)} />;
  const su = m(/^\/app\/furnizori\/(\d+)$/);
  if (su) return <EntityPage kind="supplier" id={Number(su)} />;
  const cpv = m(/^\/app\/preturi\/([0-9]{8}-[0-9])$/);
  if (cpv) return <CpvPage code={cpv} />;
  switch (path) {
    case '/app':
      return <HomePage />;
    case '/app/achizitii':
      return <SearchPage kind="acq" />;
    case '/app/produse':
      return <SearchPage kind="item" />;
    case '/app/preturi':
      return <PricesPage />;
    case '/app/alerte':
      return <AlertsPage />;
    case '/app/notificari':
      return <NotificationsPage />;
    case '/app/echipa':
      return <TeamPage />;
    case '/app/roluri':
      return <RolesPage />;
    case '/app/setari':
      return <SettingsPage />;
    case '/app/abonament':
      return <BillingPage />;
    case '/app/cont':
      return <AccountPage />;
    default:
      return (
        <Page title="Pagina nu există">
          <p className="text-sm text-gri-700">{path}</p>
        </Page>
      );
  }
}
